import React, { useState } from 'react';
import { makeStyles } from '@mui/styles';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, List, ListItem, ListItemText, TextField, IconButton, FormControl, MenuItem, Select, Collapse, Tooltip, Box } from '@mui/material';
import Delete from '@mui/icons-material/Delete';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ExpandLess from '@mui/icons-material/ExpandLess';
import OpenInNew from '@mui/icons-material/OpenInNew';
import { actionCreators } from 'src/reducers/common/trainings';
import { useDispatch, useSelector } from 'react-redux';
import translate from 'src/helpers/translate';
import GeoGebraApplet, { getGeoGebraMaterialId } from 'src/components/common/GeoGebraApplet';

const useStyles = makeStyles((theme) => ({
    root: {
        '& > *': {
            margin: theme.spacing(1),
            width: '25ch',
        },
    },
}));

export default function TrainingsDialog({ trainings, open, onClose, canEdit, topicId, problemId }) {
    const classes = useStyles();

    const dispatch = useDispatch();

    const [selectedTopicId, setSelectedTopicId] = useState(topicId);

    // Which training is currently expanded to show its embedded GeoGebra applet.
    const [expandedId, setExpandedId] = useState(null);

    const innerTopics = useSelector(state => state.common.topics.filter(t => t.id.toString().startsWith(topicId)));

    const [description, setDescription] = useState('');

    const [link, setLink] = useState('');

    const canBeSubmitted = description != '' && link != '';

    const handleAdd = () => {
        if (canBeSubmitted) {
            setLink('');
            setDescription('');
            dispatch(actionCreators.addTraining(description, link, topicId, problemId));
        }
    }

    const handleDelete = (id) => {
        dispatch(actionCreators.deleteTraining(id));
    }

    const onInnerTopicChange = (event) => {
        setSelectedTopicId(event.target.value);
    }

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth={expandedId ? 'lg' : 'md'} aria-labelledby="form-dialog-title">
            <DialogTitle id="form-dialog-title">{translate('ProblemResources_Trainings')}</DialogTitle>
            <DialogContent>
                {canEdit && (
                    <form className={classes.root} noValidate autoComplete="off">
                        <TextField required id="standard-required" value={description} label={translate("Description")} onChange={(event) => setDescription(event.target.value)} />
                        <TextField required id="standard-required" value={link} label={translate("Link")} onChange={(event) => setLink(event.target.value)} />
                        {topicId && innerTopics && (
                            <FormControl style={{ marginTop: 23 }}>
                                <Select
                                    labelId="demo-simple-select-label"
                                    id="demo-simple-select"
                                    value={selectedTopicId}
                                    onChange={onInnerTopicChange}
                                >
                                    {innerTopics && innerTopics.map(innerTopic => (
                                        <MenuItem key={`innerTopic_${innerTopic.id}`} value={innerTopic.id}>{translate(`TopicResources_${innerTopic.id}`)}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                        <Button disabled={!canBeSubmitted} onClick={handleAdd} style={{ marginTop: 20 }} variant="standard" color="primary" href="#outlined-buttons">
                            {translate("Add")}
                        </Button>
                    </form>
                )}
                <div style={{ maxHeight: '70vh', minHeight: 100, minWidth: 400, overflowY: 'auto' }}>
                    <List component="nav">
                        {trainings.map(training => {
                            const materialId = getGeoGebraMaterialId(training.url);
                            const isExpanded = expandedId === training.id;
                            return (
                                <React.Fragment key={training.id}>
                                    <ListItem>
                                        {materialId ? (
                                            <Button
                                                onClick={() => setExpandedId(isExpanded ? null : training.id)}
                                                endIcon={isExpanded ? <ExpandLess /> : <ExpandMore />}
                                            >
                                                <ListItemText primary={training.name} />
                                            </Button>
                                        ) : (
                                            <Button href={training.url} target="_blank" rel="noopener noreferrer">
                                                <ListItemText primary={training.name} />
                                            </Button>
                                        )}
                                        <Tooltip title={translate('Open') || 'Open in GeoGebra'}>
                                            <IconButton component="a" href={training.url} target="_blank" rel="noopener noreferrer" size="large">
                                                <OpenInNew fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        {canEdit && (
                                            <IconButton onClick={() => handleDelete(training.id)} size="large">
                                                <Delete />
                                            </IconButton>
                                        )}
                                    </ListItem>
                                    {materialId && (
                                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                            <Box sx={{ px: 2, pb: 2 }}>
                                                <GeoGebraApplet materialId={materialId} />
                                            </Box>
                                        </Collapse>
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </List>
                </div>
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={onClose}
                    color="primary">
                    {translate('ProfileResources_Close')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}